import { openRivalRematch } from "./rematch-navigation.js";
import { displaySides } from "./comparison-presentation.js";
import type { ComparisonDetail } from "../../../shared/contracts/product-pages.js";
import { useState } from "react";
import { useRivalryCalendar } from "./use-rivalry-calendar.js";
import { RivalryCalendarControls } from "./rivalry-calendar-controls.js";
import { rivalInsightForContext } from "../../../shared/presentation/rival-insight.js";
import {
  readInsightSelection,
  type InsightSelection,
} from "./insight-navigation.js";
import { clearInsight } from "./insight-navigation-browser.js";
import { InsightEvidence } from "./insight-evidence.js";
import type {
  ComparisonFilters,
  ComparisonRelation,
} from "../../../shared/contracts/product-pages.js";
import { ChevronLeft, Flag } from "../../shell/approved-icons.js";
import { useComparisonDetail } from "./comparison-api.js";
import { ComparisonFilterBar } from "./comparison-filters.js";
import { ComparisonRecordBoard } from "./comparison-observation.js";
import {
  ComparisonSection,
  type ComparisonTopic,
} from "./comparison-sections.js";
import "../../styles/comparison-approved.css";
import "../../styles/comparison-analysis.css";
import "../../styles/ux-refinements.css";

export interface ComparisonActions {
  onOpenRoundId?(id: string): void;
  onOpenInsights?(id: string): void;
  onStartRound?(): void;
}
interface DetailPageProps extends ComparisonActions {
  relation: ComparisonRelation;
  revision?: string;
  onBack(): void;
  availableYears?: number[];
  hasSimulator?: boolean;
}

function useDetailModel(props: DetailPageProps) {
  const [selection, setSelection] = useState(() =>
    readInsightSelection(window.location.search),
  );
  const calendar = useRivalryCalendar();
  const [analysisFilters, setFilters] = useState<ComparisonFilters>(
    selection?.filters ?? { basis: "gross" },
  );
  const filters = { ...analysisFilters, ...calendar.filters };
  const [topic, setTopic] = useState<ComparisonTopic>(
    selection?.topic ?? "overview",
  );
  const [attempt, setAttempt] = useState(0);
  const resource = useComparisonDetail(
    props.relation.id,
    filters,
    `${props.revision ?? ""}:${attempt}`,
  );
  return {
    filters,
    topic,
    setTopic,
    selection,
    resource,
    retry: () => setAttempt((value) => value + 1),
    change: (next: ComparisonFilters) => {
      setFilters(next);
      setSelection(null);
      clearInsight();
    },
  };
}

export function ComparisonDetailPage(
  props: DetailPageProps,
): React.JSX.Element {
  const model = useDetailModel(props);
  const { resource, topic } = model;
  return (
    <div className="r3-root rc-root">
      <DetailHero relation={props.relation} onBack={props.onBack} />
      <DetailSummary {...props} model={model} />
      <DetailTabs topic={topic} onChange={model.setTopic} />
      <div className="r3-content">
        <ComparisonLoadState
          loading={resource.loading}
          error={resource.error}
          onRetry={model.retry}
        />
        {resource.data ? (
          <DetailAnalysis
            topic={topic}
            detail={resource.data}
            selection={model.selection}
            onOpenRoundId={props.onOpenRoundId}
          />
        ) : null}
      </div>
    </div>
  );
}

function DetailSummary(
  props: DetailPageProps & { model: ReturnType<typeof useDetailModel> },
) {
  const { resource, filters, change } = props.model;
  return (
    <div className="r3-content">
      <RivalryCalendarControls
        availableYears={props.availableYears ?? []}
        hasSimulator={
          props.hasSimulator ??
          resource.catalog?.sources.some(
            (source) => source.environment === "simulator",
          ) ??
          false
        }
        onChange={() => change({ basis: filters.basis ?? "gross" })}
      />
      {resource.data ? (
        <ComparisonRecordBoard
          relation={resource.data.relation}
          record={resource.data.record}
          sharedRounds={
            resource.data.sources.filter(
              (source) => source.detail !== "measured",
            ).length
          }
        />
      ) : null}
      <DetailActions {...props} detail={resource.catalog} />
      <ComparisonFilterBar
        filters={filters}
        catalog={resource.catalog}
        onChange={change}
      />
    </div>
  );
}

function DetailTabs(props: {
  topic: ComparisonTopic;
  onChange(topic: ComparisonTopic): void;
}) {
  return (
    <nav className="rc-tabs" aria-label="Rivalry detail">
      {(["overview", "history", "holes", "conditions"] as const).map(
        (value) => (
          <button
            key={value}
            aria-pressed={props.topic === value}
            onClick={() => props.onChange(value)}
          >
            {value === "history"
              ? "Rounds"
              : value[0]?.toUpperCase() + value.slice(1)}
          </button>
        ),
      )}
    </nav>
  );
}

function DetailAnalysis(props: {
  topic: ComparisonTopic;
  detail: ComparisonDetail;
  selection: InsightSelection | null;
  onOpenRoundId?: ((id: string) => void) | undefined;
}): React.JSX.Element {
  const finding = props.selection
    ? rivalInsightForContext(props.detail, props.selection.context)
    : null;
  return (
    <>
      {finding ? (
        <InsightEvidence
          finding={finding}
          detail={props.detail}
          onOpenRoundId={props.onOpenRoundId}
        />
      ) : props.selection ? (
        <p className="rc-note" role="status">
          That insight no longer has the same qualified evidence. Your current
          shared record is shown below.
        </p>
      ) : null}
      <ComparisonSection
        topic={props.topic}
        detail={props.detail}
        onOpenRoundId={props.onOpenRoundId}
      />
    </>
  );
}

function DetailHero({
  relation,
  onBack,
}: {
  relation: ComparisonRelation;
  onBack(): void;
}): React.JSX.Element {
  return (
    <header className="r3-hero r3-hero--detail">
      <button className="r3-back" onClick={onBack}>
        <ChevronLeft size={16} />
        All rivalries
      </button>
      <div className="r3-hero-copy">
        <p className="r3-kicker">YOUR SHARED RECORD</p>
        <h1>
          {relation[displaySides(relation)[0]].name}
          <em>& {relation[displaySides(relation)[1]].name}</em>
        </h1>
      </div>
    </header>
  );
}

function DetailActions(
  props: ComparisonActions & {
    relation: ComparisonRelation;
    detail: ComparisonDetail | null;
  },
): React.JSX.Element {
  const canRematch = !!props.detail?.sources.some(
    (source) => source.state === "completed",
  );
  return (
    <div className="rc-actions">
      {props.onStartRound ? (
        <button
          className="rc-action rc-action-primary"
          onClick={() =>
            canRematch && props.detail
              ? openRivalRematch(props.detail)
              : props.onStartRound?.()
          }
        >
          <Flag size={16} />
          {canRematch ? "Play again" : "Start a round"}
        </button>
      ) : null}
    </div>
  );
}

export function ComparisonLoadState({
  loading,
  error,
  onRetry,
}: {
  loading: boolean;
  error: string | null;
  onRetry(): void;
}): React.JSX.Element | null {
  if (loading)
    return (
      <p className="rc-state" role="status">
        Loading your shared record…
      </p>
    );
  if (error)
    return (
      <div className="rc-state" role="alert">
        <p>{error}</p>
        <button className="rc-action" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  return null;
}
